// VideoManipulator.js
import * as tf from '@tensorflow/tfjs-core';
import '@tensorflow/tfjs-backend-webgl';
import '@tensorflow/tfjs-backend-wasm';
import * as bodySegmentation from '@tensorflow-models/body-segmentation';
import logger from './logger';
import { getImage } from './db';
import { createSnack } from '../actions/SnackActions';
import { MaskProcessor } from './MaskProcessor';
import { preProcessFrame, upscaleImageData } from './imageProcessing';

async function initializeBackend() {
  // prefer 'webgl' backend for performance; fallback to 'wasm'
  if (tf.backend().backendName !== 'webgl') {
    await tf.setBackend('wasm');
  }
  await tf.ready();
  logger.log(`Using backend: ${tf.getBackend()}`);
}
initializeBackend();

let segmenter = null;
let isLoadingSegmenter = false;

const model = bodySegmentation.SupportedModels.MediaPipeSelfieSegmentation;
const segmenterConfig = {
  runtime: 'mediapipe',
  modelType: 'landscape',
  solutionPath: '/selfie_segmentation',
};

class VideoManipulator {
  constructor(stream, simple = false) {
    this.video = document.createElement('video');
    this.video.autoplay = true;
    this.video.muted = true;
    this.canvas = document.createElement('canvas');
    this.ctx = this.canvas.getContext('2d');
    this.offscreenCanvas = document.createElement('canvas');
    this.offscreenCtx = this.offscreenCanvas.getContext('2d');
    this.effect = null;
    this.backgroundImage = null;
    this.simple = simple;
    this.stop = false;
    this._tick = this._tick.bind(this);

    if (simple && stream) {
      this.outputStream = stream;
    } else {
      this.outputStream = this.canvas.captureStream();
      if (stream) {
        this.setInputStream(stream);
      }
    }

    // Segmentation caching: run segmentation at ~10 fps.
    this.lastSegmentationTime = 0;
    this.cachedSegmentation = null;

    // Initialize MaskProcessor with current canvas dimensions.
    this.maskProcessor = new MaskProcessor(
      this.canvas.width,
      this.canvas.height
    );

    // Append the canvas to the DOM.
    document.body.appendChild(this.canvas);
  }

  async setEffect(effect, dispatch) {
    if (this.simple) return;
    try {
      this.effect = effect;
      if (effect?.type === 'blur' || effect?.type === 'virtual-background') {
        await this._loadSegmentorNet();
      }
      if (effect?.type === 'virtual-background' && effect?.image) {
        let image = effect?.image;
        this.backgroundImage = document.createElement('img');
        if (typeof image === 'string' && image.startsWith('indexeddb://')) {
          this.backgroundImage.src = await getImage(
            image.replace('indexeddb://', '')
          );
        } else if (typeof image === 'string' && image.startsWith('http')) {
          this.backgroundImage.src = `/api/image?url=${image}`;
        } else {
          this.backgroundImage.src = image;
        }
        await new Promise((resolve, reject) => {
          this.backgroundImage.onload = resolve;
          this.backgroundImage.onerror = reject;
        });
      }
    } catch (err) {
      createSnack(dispatch, 'Error setting effect', 'error');
      throw new Error(`Error setting effect: ${err.message}`);
    }
  }

  async setInputStream(stream) {
    this.stop = true;
    if (!stream) {
      this.destroy();
      return;
    }
    this.video.srcObject = stream;
    this.video.onplaying = () => {
      this.video.width = this.video.videoWidth;
      this.video.height = this.video.videoHeight;
      this.canvas.width = this.video.videoWidth;
      this.canvas.height = this.video.videoHeight;
      this.offscreenCanvas.width = this.video.videoWidth;
      this.offscreenCanvas.height = this.video.videoHeight;
      // Update MaskProcessor dimensions when the video size is known.
      this.maskProcessor.updateDimensions(
        this.canvas.width,
        this.canvas.height
      );
      this.stop = false;
      this._tick();
    };

    const playPromise = this.video.play();
    if (playPromise !== undefined) {
      playPromise
        .then(() => {
          // Automatic playback started.
          // Continue with any necessary class operations.
          if (
            this.effect?.type === 'blur' ||
            this.effect?.type === 'virtual-background'
          ) {
            this._loadSegmentorNet().catch((err) =>
              logger.error('Failed to load segmentor net:', err)
            );
          }
        })
        .catch((error) => {
          // Playback was prevented or interrupted.
          if (error.name === 'AbortError') {
            // Specific handling for AbortError if needed.
            logger.log('Video play aborted:', error);
          } else {
            logger.error('Error playing video:', error);
          }
          // Optionally update internal state or retry as needed.
        });
    }
  }

  destroy() {
    if (this.video.srcObject) {
      this.video.srcObject = null;
    }
    this.stop = true;
  }

  getOutputStream() {
    return this.outputStream;
  }

  getCanvas() {
    return this.canvas;
  }

  _scheduleNextFrame(startTime) {
    const frameTime = 1000 / 25;
    const elapsedTime = Date.now() - startTime;
    setTimeout(this._tick, Math.max(12, frameTime - elapsedTime));
  }

  _drawOriginalFrame() {
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    this.ctx.drawImage(this.video, 0, 0, this.canvas.width, this.canvas.height);
  }

  async _loadSegmentorNet() {
    if (segmenter || isLoadingSegmenter) return;
    isLoadingSegmenter = true;
    logger.log('Loading segmenter...');
    try {
      segmenter = await bodySegmentation.createSegmenter(
        model,
        segmenterConfig
      );
      logger.log('Segmenter loaded successfully!');
    } catch (err) {
      logger.error('Error loading segmenter:', err);
      segmenter = null;
    } finally {
      isLoadingSegmenter = false;
    }
  }

  async _tick() {
    if (this.stop) return;
    const dt = Date.now();
    const effect = this.effect;

    if (!effect) {
      this._drawOriginalFrame();
      this._scheduleNextFrame(dt);
      return;
    }

    if (!segmenter) {
      logger.log('Segmenter is not ready yet, retrying...');
      setTimeout(this._tick, 50);
      return;
    }

    // Preprocess frame using the utility function.
    const preprocessedFrameData = preProcessFrame(
      this.video,
      this.offscreenCanvas.width,
      this.offscreenCanvas.height
    );

    // Run segmentation only every 100ms (~10 fps).
    const now = Date.now();
    const segmentationInterval = 100;
    if (
      !this.lastSegmentationTime ||
      now - this.lastSegmentationTime >= segmentationInterval
    ) {
      this.cachedSegmentation = await segmenter.segmentPeople(
        preprocessedFrameData
      );
      this.lastSegmentationTime = now;
    }
    const segmentation = this.cachedSegmentation;

    // --- COMMON: Process the segmentation mask using MaskProcessor ---
    let processedMaskData;
    {
      const maskObj = segmentation[0];
      let rawMaskData = await maskObj.mask.toImageData();
      // Upscale if necessary.
      if (
        rawMaskData.width !== this.canvas.width ||
        rawMaskData.height !== this.canvas.height
      ) {
        rawMaskData = upscaleImageData(
          rawMaskData,
          this.canvas.width,
          this.canvas.height
        );
      }
      if (
        this.maskProcessor.targetWidth !== this.canvas.width ||
        this.maskProcessor.targetHeight !== this.canvas.height
      ) {
        this.maskProcessor.updateDimensions(
          this.canvas.width,
          this.canvas.height
        );
      }
      processedMaskData = this.maskProcessor.process(rawMaskData);
    }

    // --- EFFECT-SPECIFIC Processing ---
    if (effect.type === 'blur') {
      // Use the processed mask (with EMA integrated in MaskProcessor) for blur.
      const processedMaskObj = {
        mask: { toImageData: () => processedMaskData },
      };
      const processedSegmentation = [processedMaskObj];
      this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
      this.ctx.drawImage(
        this.video,
        0,
        0,
        this.canvas.width,
        this.canvas.height
      );
      bodySegmentation.drawBokehEffect(
        this.canvas,
        this.video,
        processedSegmentation,
        0.9,
        effect.strength || 12,
        1,
        false
      );
    } else if (
      effect.type === 'virtual-background' &&
      this.backgroundImage &&
      this.backgroundImage.complete &&
      this.video.width &&
      this.canvas.width
    ) {
      this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
      this.ctx.drawImage(
        this.backgroundImage,
        0,
        0,
        this.canvas.width,
        this.canvas.height
      );
      const backgroundData = this.ctx.getImageData(
        0,
        0,
        this.canvas.width,
        this.canvas.height
      );
      this.ctx.drawImage(
        this.video,
        0,
        0,
        this.canvas.width,
        this.canvas.height
      );
      const foregroundData = this.ctx.getImageData(
        0,
        0,
        this.canvas.width,
        this.canvas.height
      );
      const outputData = this.ctx.createImageData(
        this.canvas.width,
        this.canvas.height
      );
      const numPixels = outputData.data.length;
      for (let i = 0; i < numPixels; i += 4) {
        const maskAlpha = processedMaskData.data[i + 3] / 255;
        outputData.data[i] =
          foregroundData.data[i] * maskAlpha +
          backgroundData.data[i] * (1 - maskAlpha);
        outputData.data[i + 1] =
          foregroundData.data[i + 1] * maskAlpha +
          backgroundData.data[i + 1] * (1 - maskAlpha);
        outputData.data[i + 2] =
          foregroundData.data[i + 2] * maskAlpha +
          backgroundData.data[i + 2] * (1 - maskAlpha);
        outputData.data[i + 3] = 255;
      }
      this.ctx.putImageData(outputData, 0, 0);
    } else {
      this._drawOriginalFrame();
    }

    this._scheduleNextFrame(dt);
  }
}

export default VideoManipulator;
